// import React, { useState } from "react";
import { Input } from '@/components/ui-kit/input';
// import { ColorSwatch, validHexaColorReg } from "@/components/color-swatch/color-swatch";
import { useTranslation } from 'react-i18next';
import { useWidgetColorSuggestion } from '@/modules/gpt-chats/hooks/use-agents';
import { useProjectStore } from '@/modules/identifier/state/use-project-store';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Button } from '@/components/ui-kit/button';
import { WidgetColorPalette } from '@/modules/gpt-chats/models/widget.model';
import { Loader } from 'lucide-react';
import { useMemo } from 'react';

interface FormValues {
  url: string;
  brand_color: string;
}

type ColorSuggestionFormProps = {
  values: { url: string; brand_color: string };
  onSuggestion: (args: {
    values: { url: string; brand_color: string };
    suggestion: WidgetColorPalette;
  }) => void;
};

export const ColorSuggestionForm: React.FC<ColorSuggestionFormProps> = ({
  onSuggestion,
  values,
}) => {
  const { t } = useTranslation();
  const tenantId = useProjectStore().selectedProject?.tenantId || '';
  const { mutateAsync, isPending } = useWidgetColorSuggestion();

  const schema = useMemo(() => z.object({
    url: z
      .string()
      .trim()
      .nonempty({ message: t('SITE_URL_REQUIRED') })
      .url({ message: t('INVALID_URL') }),
    brand_color: z.string().trim().optional(),
  }), [t]);

  const form = useForm<FormValues>({
    values: values || { url: '', brand_color: '' },
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      const suggestions = await mutateAsync({
        url: values.url,
        brand_color: values.brand_color,
        project_key: tenantId,
      });
      onSuggestion({ values, suggestion: suggestions });
    } catch (error) {
      console.error('Failed to get color suggestions', error);
    }
  };

  const { isValid } = form.formState;

  return (
    <Form {...form}>
      <div className="grid gap-4">
        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel htmlFor="site-url">{t('SITE_URL')}</FormLabel>
              <FormControl>
                <Input id="site-url" placeholder={t('ENTER_SITE_URL_PLACEHOLDER')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="button"
          variant="outline"
          onClick={form.handleSubmit(onSubmit)}
          disabled={form.formState.isSubmitting || !isValid || isPending}
        >
          {isPending && <Loader className="mr-2 aspect-square w-4 animate-spin" />}
          <span>{t('GENERATE_PALETTE')}</span>
        </Button>
        <div className="text-sm text-medium-emphasis">
          {t('GENERATE_PALETTE_HINT')}
        </div>
      </div>
    </Form>
  );
};
